/**
 * ComponentCoverageSelector
 *
 * Per-component coverage slot selector. Shows coverage dropdowns for template
 * components that have coverage slot requirements defined.
 *
 * Only displays slots that the template requires (REQUIRED or OPTIONAL status).
 */

import { ShieldCheckIcon } from '@heroicons/react/24/outline';
import React, { useCallback, useMemo } from 'react';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useGetCoveragesQuery } from '@/services/api/coverageService';
import {
  CoverageCategory,
  CoverageSchema,
  IdentifierType,
} from '@/services/types/coverage-types';

import type { CoverageSlotName } from './CoverageSlotSelector';
// These types were removed from the generated service when templates migrated to v2.
// Defined locally for backward compatibility — components that still track coverage
// slot requirements will continue to work as long as the backend populates the field.
export type SlotRequirementLevel = 'required' | 'optional' | 'not_used';
export type RequiredEntitySlotsSchema = {
  coverage_1p?: SlotRequirementLevel;
  coverage_3p?: SlotRequirementLevel;
  coverage_health?: SlotRequirementLevel;
  medical_providers?: SlotRequirementLevel;
};

// Types are defined above and already exported

/**
 * Coverage selections for a component.
 */
export interface ComponentCoverageSelections {
  coverage_1p_id?: string | null;
  coverage_3p_id?: string | null;
  coverage_health_id?: string | null;
}

interface ComponentCoverageSelectorProps {
  /** The matter ID to fetch coverages for */
  matterId: string;
  /** Template's required entity slots - determines which selectors to show */
  requiredSlots?: RequiredEntitySlotsSchema | null;
  /** Current coverage selections for the component */
  selections?: ComponentCoverageSelections;
  /** Callback when a slot selection changes */
  onSlotChange: (slotName: CoverageSlotName, coverageId: string | null) => void;
  /** Optional className for the container */
  className?: string;
  /** Whether the component is disabled */
  disabled?: boolean;
  /** Compact mode - smaller, inline display */
  compact?: boolean;
}

/**
 * Maps coverage categories to their corresponding slot names.
 */
const CATEGORY_TO_SLOT: Record<CoverageCategory, CoverageSlotName | null> = {
  [CoverageCategory.FIRST_PARTY]: '1p',
  [CoverageCategory.THIRD_PARTY]: '3p',
  [CoverageCategory.FIRST_PARTY_HEALTH]: 'health',
  [CoverageCategory.NOT_LIABLE_INVOLVED_PARTY]: null,
  [CoverageCategory.UNCLEAR]: null,
  [CoverageCategory.PROPERTY]: null,
  [CoverageCategory.WORKERS_COMPENSATION]: null,
};

/**
 * Maps slot names to API field names.
 */
const SLOT_TO_API_FIELD: Record<CoverageSlotName, keyof ComponentCoverageSelections> = {
  '1p': 'coverage_1p_id',
  '3p': 'coverage_3p_id',
  'health': 'coverage_health_id',
};

/**
 * Maps slot names to required_entity_slots field names.
 */
const SLOT_TO_REQUIREMENT_FIELD: Record<CoverageSlotName, keyof RequiredEntitySlotsSchema> = {
  '1p': 'coverage_1p',
  '3p': 'coverage_3p',
  'health': 'coverage_health',
};

/**
 * Display configuration for each slot.
 */
const SLOT_CONFIG: Record<
  CoverageSlotName,
  {
    label: string;
    shortLabel: string;
    emptyText: string;
  }
> = {
  '1p': {
    label: '1P Coverage',
    shortLabel: '1P',
    emptyText: 'No 1P coverage',
  },
  '3p': {
    label: '3P Coverage',
    shortLabel: '3P',
    emptyText: 'No 3P coverage',
  },
  health: {
    label: 'Health Coverage',
    shortLabel: 'Health',
    emptyText: 'No health coverage',
  },
};

/**
 * Get a display name for a coverage.
 */
const getCoverageDisplayName = (coverage: CoverageSchema): string => {
  return coverage.carrier?.name || 'Unknown Carrier';
};

/**
 * Get the primary policy number from a coverage.
 */
const getPolicyNumber = (coverage: CoverageSchema): string | null => {
  if (
    !coverage.policy_identifiers ||
    coverage.policy_identifiers.length === 0
  ) {
    return null;
  }
  const policyNumber = coverage.policy_identifiers.find(
    (id) => id.identifier_type === IdentifierType.POLICY_NUMBER,
  );
  return (policyNumber || coverage.policy_identifiers[0])?.identifier_value || null;
};

/**
 * Compact slot selector for inline display in component items.
 */
interface CompactSlotSelectorProps {
  slotName: CoverageSlotName;
  isRequired: boolean;
  coverages: CoverageSchema[];
  selectedCoverageId: string | null;
  onSelect: (coverageId: string | null) => void;
  disabled?: boolean;
}

const CompactSlotSelector: React.FC<CompactSlotSelectorProps> = ({
  slotName,
  isRequired,
  coverages,
  selectedCoverageId,
  onSelect,
  disabled = false,
}) => {
  const config = SLOT_CONFIG[slotName];
  const selectedCoverage = coverages.find((c) => c.id === selectedCoverageId);

  const handleValueChange = useCallback(
    (value: string) => {
      if (value === '__none__') {
        onSelect(null);
      } else {
        onSelect(value);
      }
    },
    [onSelect],
  );

  const hasOptions = coverages.length > 0;
  const selectId = `component-coverage-${slotName}`;

  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center gap-1 min-w-[50px]">
        <span
          id={`${selectId}-label`}
          className={cn(
            'text-[10px] font-medium uppercase',
            isRequired ? 'text-amber-600' : 'text-zinc-400',
          )}
        >
          {config.shortLabel}
          {isRequired && <span className="text-amber-500">*</span>}
        </span>
      </div>
      <Select
        value={selectedCoverageId || '__none__'}
        onValueChange={handleValueChange}
        disabled={disabled || !hasOptions}
      >
        <SelectTrigger
          aria-labelledby={`${selectId}-label`}
          className={cn(
            'h-7 text-xs flex-1 min-w-0',
            !hasOptions && 'text-zinc-400',
            selectedCoverage && 'border-indigo-200 bg-indigo-50/30',
            isRequired && !selectedCoverage && 'border-amber-200 bg-amber-50/30',
          )}
        >
          <SelectValue placeholder={config.emptyText}>
            {selectedCoverage && (
              <span className="flex items-center gap-1.5 truncate">
                <ShieldCheckIcon className="h-3 w-3 text-indigo-600 flex-shrink-0" />
                <span className="truncate text-xs">
                  {getCoverageDisplayName(selectedCoverage)}
                </span>
              </span>
            )}
            {!selectedCoverage && hasOptions && (
              <span className="text-zinc-400">Select...</span>
            )}
            {!selectedCoverage && !hasOptions && (
              <span className="text-zinc-400">{config.emptyText}</span>
            )}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {hasOptions && (
            <SelectItem value="__none__" className="text-zinc-500 text-xs">
              No selection
            </SelectItem>
          )}
          {coverages.map((coverage) => {
            const policyNumber = getPolicyNumber(coverage);
            const displayName = getCoverageDisplayName(coverage);

            return (
              <SelectItem
                key={coverage.id}
                value={coverage.id}
                className="py-1.5 text-xs"
              >
                <div className="flex flex-col gap-0.5">
                  <span className="font-medium">{displayName}</span>
                  {policyNumber && (
                    <span className="text-[10px] text-zinc-500">
                      Policy: {policyNumber}
                    </span>
                  )}
                </div>
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
    </div>
  );
};

/**
 * ComponentCoverageSelector Component
 *
 * Renders coverage dropdowns based on template's required_entity_slots.
 * Only shows dropdowns for slots that are "required" or "optional".
 */
export const ComponentCoverageSelector: React.FC<ComponentCoverageSelectorProps> = ({
  matterId,
  requiredSlots,
  selections = {},
  onSlotChange,
  className,
  disabled = false,
  compact = true,
}) => {
  const { data: coverages, isLoading, error } = useGetCoveragesQuery(
    { matterId },
    { skip: !matterId },
  );

  // Determine which slots to show based on template requirements
  const slotsToShow = useMemo(() => {
    if (!requiredSlots) return [];

    const result: Array<{ slotName: CoverageSlotName; isRequired: boolean }> = [];
    const slotOrder: CoverageSlotName[] = ['1p', '3p', 'health'];

    slotOrder.forEach((slotName) => {
      const requirementField = SLOT_TO_REQUIREMENT_FIELD[slotName];
      const level = requiredSlots[requirementField];

      if (level === 'required' || level === 'optional') {
        result.push({
          slotName,
          isRequired: level === 'required',
        });
      }
    });

    return result;
  }, [requiredSlots]);

  // Group coverages by slot
  const coveragesBySlot = useMemo(() => {
    const result: Record<CoverageSlotName, CoverageSchema[]> = {
      '1p': [],
      '3p': [],
      health: [],
    };

    if (!coverages) return result;

    coverages.forEach((coverage) => {
      const slotName = coverage.category
        ? CATEGORY_TO_SLOT[coverage.category]
        : null;
      if (slotName) {
        result[slotName].push(coverage);
      }
    });

    return result;
  }, [coverages]);

  // Get current selection for a slot
  const getSelectionForSlot = useCallback(
    (slotName: CoverageSlotName): string | null => {
      const apiField = SLOT_TO_API_FIELD[slotName];
      return selections[apiField] ?? null;
    },
    [selections],
  );

  const handleSlotChange = useCallback(
    (slotName: CoverageSlotName) => (coverageId: string | null) => {
      onSlotChange(slotName, coverageId);
    },
    [onSlotChange],
  );

  // Don't render if no slots to show
  if (slotsToShow.length === 0) {
    return null;
  }

  if (!matterId) {
    return null;
  }

  if (isLoading) {
    return (
      <div className={cn('flex items-center gap-1.5', className)}>
        <div className="h-3 w-3 animate-spin rounded-full border border-zinc-200 border-t-indigo-600" />
        <span className="text-[10px] text-zinc-400">Loading coverages...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={cn('text-[10px] text-red-500', className)}>
        Failed to load coverages
      </div>
    );
  }

  if (compact) {
    return (
      <div className={cn('space-y-2', className)}>
        {slotsToShow.map(({ slotName, isRequired }) => (
          <CompactSlotSelector
            key={slotName}
            slotName={slotName}
            isRequired={isRequired}
            coverages={coveragesBySlot[slotName]}
            selectedCoverageId={getSelectionForSlot(slotName)}
            onSelect={handleSlotChange(slotName)}
            disabled={disabled}
          />
        ))}
      </div>
    );
  }

  // Non-compact mode (if needed in future)
  return (
    <div className={cn('space-y-3', className)}>
      <div className="flex items-center gap-1.5">
        <ShieldCheckIcon className="h-3.5 w-3.5 text-zinc-400" />
        <span className="text-xs font-medium text-zinc-700">Coverage Mapping</span>
      </div>
      <div className="space-y-2">
        {slotsToShow.map(({ slotName, isRequired }) => (
          <CompactSlotSelector
            key={slotName}
            slotName={slotName}
            isRequired={isRequired}
            coverages={coveragesBySlot[slotName]}
            selectedCoverageId={getSelectionForSlot(slotName)}
            onSelect={handleSlotChange(slotName)}
            disabled={disabled}
          />
        ))}
      </div>
    </div>
  );
};

/**
 * Helper to check if a component has any coverage slot requirements.
 */
export function hasAnyCoverageSlotRequirements(
  requiredSlots: RequiredEntitySlotsSchema | null | undefined,
): boolean {
  if (!requiredSlots) return false;

  return (
    requiredSlots.coverage_1p === 'required' ||
    requiredSlots.coverage_1p === 'optional' ||
    requiredSlots.coverage_3p === 'required' ||
    requiredSlots.coverage_3p === 'optional' ||
    requiredSlots.coverage_health === 'required' ||
    requiredSlots.coverage_health === 'optional'
  );
}

/**
 * Maps a CoverageSlotName to the corresponding API field name.
 */
export function slotNameToApiField(slotName: CoverageSlotName): keyof ComponentCoverageSelections {
  return SLOT_TO_API_FIELD[slotName];
}

export default ComponentCoverageSelector;
